import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { newCustomerSchema, type NewCustomerFormInputs } from '../../schemas/customer.schemas';
import { useCreateCustomerMutation } from '../../hooks/useCreateCustomerMutation';
import {
    fetchCustomerById,
    formInputsToCustomer,
    isDuplicateInScopeError,
    type DuplicateInScopeError,
} from '../../services/customers';
import { usePermissions } from '@/features/auth';
import { AddressLookup } from '@/features/address-lookup';
import type { NormalisedAddress } from '@/features/address-lookup';
import { Form } from '@/shared/components/form';
import { Button } from '@/shared/components/ui/button/Button';
import { Label, Input, Select } from '@/shared/components/form';
import { Alert } from '@/shared/components';
import { DuplicateCustomerNotice } from './DuplicateCustomerNotice';
import { TITLE_OPTIONS } from '../fields/titleOptions';
import type { Customer } from '../../types/customers.types';

interface NewCustomerFormProps {
    onSubmit: (customer: Customer) => void;
    defaultValues?: Partial<NewCustomerFormInputs>;
    /**
     * Controls when the customer record is persisted to the backend.
     *
     * - `'immediate'` (default): POSTs to `/customers/` on form submit. Use
     *   this on standalone create pages where persistence is the primary intent.
     * - `'deferred'`: Skips the network call and passes a pending `Customer`
     *   (no `id`) to `onSubmit`. Use this inside the quote flow: the pending
     *   customer travels with the quote as JSON and is never persisted from the
     *   quote journey. The canonical customer record is resolve-or-created
     *   downstream by agreements-service when the Agreement is created (see
     *   #644), so the quote journey must not `POST /customers/` itself.
     */
    mode?: 'immediate' | 'deferred';
}

export const NewCustomerForm: React.FC<NewCustomerFormProps> = ({
    onSubmit,
    defaultValues = {},
    mode = 'immediate',
}) => {
    const createCustomerMutation = useCreateCustomerMutation();

    const {
        handleSubmit,
        control, // For Controller with DatePicker or other complex components
        watch, // To conditionally show/hide fields
        setValue,
        // formState: { errors },
    } = useForm<NewCustomerFormInputs>({
        resolver: zodResolver(newCustomerSchema),
        defaultValues: defaultValues,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [duplicateError, setDuplicateError] = useState<DuplicateInScopeError | null>(null);

    // `customer.read` gates the "Select existing customer" CTA: selecting a
    // candidate triggers a GET /customers/{id} (and downstream navigation to
    // the customer-detail page) which both require read. A caller with only
    // `customer.add + customer.browse` still sees the warning and can edit
    // the email — no dead-end if they click Select then 403.
    const { hasPermission } = usePermissions();
    const canReadCustomers = hasPermission({ resource: 'customer', action: 'read' });

    const watchBusinessName = watch('businessName');
    const isBusinessSelected = !!watchBusinessName; // Check if businessName has a value

    // Stable across renders — an unstable prop would re-fire effects in AddressLookup.
    const handleAddressSelected = React.useCallback(
        (address: NormalisedAddress) => {
            setValue('addressLine1', address.addressLine1, { shouldValidate: true, shouldDirty: true });
            setValue('addressLine2', address.addressLine2, { shouldValidate: true, shouldDirty: true });
            setValue('city', address.city, { shouldValidate: true, shouldDirty: true });
            setValue('postcode', address.postcode, { shouldValidate: true, shouldDirty: true });
        },
        [setValue],
    );

    const handleFormSubmit: SubmitHandler<NewCustomerFormInputs> = async (data) => {
        setSubmitError(null);
        setDuplicateError(null);

        if (mode === 'deferred') {
            // In deferred mode, build a pending Customer (no id) and hand it
            // to the parent synchronously. No network call — the quote
            // persistence layer will POST /customers/ before saving the quote.
            const pendingCustomer = formInputsToCustomer(data);
            onSubmit(pendingCustomer);
            return;
        }

        // Immediate mode — original behaviour: POST to /customers/ now.
        setIsSubmitting(true);

        try {
            // Submit to API and get the created customer with the real ID
            const createdCustomer = await createCustomerMutation.mutateAsync(data);

            // Call the parent's onSubmit with the created customer
            onSubmit(createdCustomer);
        } catch (error) {
            if (isDuplicateInScopeError(error)) {
                // Surface as a distinct yellow warning, not a generic red error.
                setDuplicateError(error);
                return;
            }

            console.error('Failed to create customer:', error);

            // Set user-friendly error message
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to create customer. Please try again.';
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectExisting = async (customerId: string) => {
        try {
            const fetchedCustomer = await fetchCustomerById(customerId);
            onSubmit(fetchedCustomer);
        } catch {
            setSubmitError('Could not load existing customer. Please try again.');
        }
    };

    return (
        <Form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="rounded-lg border border-mp-border bg-mp-surface p-4 space-y-4"
        >
            {/* Personal Details */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-mp-foreground">
                    Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="title">
                            Title <span className="text-mp-error">*</span>{' '}
                        </Label>
                        <Controller
                            name="title"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Select
                                    {...field}
                                    id="title"
                                    options={TITLE_OPTIONS}
                                    placeholder="Please select a Title"
                                    value={field.value}
                                    error={!!error}
                                    hint={error?.message}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label htmlFor="forename">
                            Forename <span className="text-mp-error">*</span>{' '}
                        </Label>
                        <Controller
                            name="forename"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    id="forename"
                                    type="text"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!error}
                                    hint={error?.message}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label htmlFor="surname">
                            Surname <span className="text-mp-error">*</span>{' '}
                        </Label>
                        <Controller
                            name="surname"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    id="surname"
                                    type="text"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!error}
                                    hint={error?.message}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            {/* Address Details */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-mp-foreground pt-4 border-t border-mp-border">
                    Address
                </h3>
                <div>
                    <Label htmlFor="addressFind">Find address</Label>
                    <AddressLookup id="addressFind" onAddressSelected={handleAddressSelected} />
                </div>
                <div>
                    <Label htmlFor="addressLine1">
                        Address Line 1 <span className="text-mp-error">*</span>{' '}
                    </Label>
                    <Controller
                        name="addressLine1"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id="addressLine1"
                                type="text"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="addressLine2">Address Line 2</Label>
                    <Controller
                        name="addressLine2"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id="addressLine2"
                                type="text"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="city">
                            City <span className="text-mp-error">*</span>{' '}
                        </Label>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    id="city"
                                    type="text"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!error}
                                    hint={error?.message}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label htmlFor="postcode">
                            Postcode <span className="text-mp-error">*</span>{' '}
                        </Label>
                        <Controller
                            name="postcode"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    id="postcode"
                                    type="text"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!error}
                                    hint={error?.message}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-mp-foreground pt-4 border-t border-mp-border">
                    Contact Details
                </h3>
                <div>
                    <Label htmlFor="email">
                        Email <span className="text-mp-error">*</span>{' '}
                    </Label>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id="email"
                                type="email"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="mobilePhone">
                        Mobile Phone <span className="text-mp-error">*</span>{' '}
                    </Label>
                    <Controller
                        name="mobilePhone"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id="mobilePhone"
                                type="tel"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Business Details (Optional) */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-mp-foreground pt-4 border-t border-mp-border">
                    Business Details (Optional)
                </h3>
                <div>
                    <Label htmlFor="businessName">Business Name</Label>
                    <Controller
                        name="businessName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id="businessName"
                                type="text"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                hint={error?.message}
                                placeholder="Enter business name if applicable"
                            />
                        )}
                    />
                </div>

                {/* VAT Number - conditionally rendered based on businessName */}
                {isBusinessSelected && (
                    <div>
                        <Label htmlFor="vatNumber">VAT Number</Label>
                        <Controller
                            name="vatNumber"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    id="vatNumber"
                                    type="text"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!error}
                                    hint={error?.message}
                                    placeholder="Enter VAT number if applicable"
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            {duplicateError && (
                <DuplicateCustomerNotice
                    duplicateError={duplicateError}
                    canReadCustomers={canReadCustomers}
                    onSelectExisting={handleSelectExisting}
                    onEditEmail={() => setDuplicateError(null)}
                />
            )}

            {/* Error Message */}
            {submitError && (
                <Alert variant="error">
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-medium">Failed to create customer</p>
                        <p>{submitError}</p>
                    </div>
                </Alert>
            )}

            <Button
                disabled={mode === 'immediate' && (isSubmitting || createCustomerMutation.isPending)}
                className="w-full mt-8"
            >
                {mode === 'immediate' && (isSubmitting || createCustomerMutation.isPending)
                    ? 'Creating Customer...'
                    : 'Add Customer'}
            </Button>
        </Form>
    );
};
